import api from './api';
import type {
  EvaluationFramework,
  EvaluationAxis,
  EvaluationCriteria,
  PerformanceLevel,
  EvaluationCycle,
  EvaluationAssignment,
  EntityEvaluation,
  CriteriaScore,
  EvaluationFormData,
  EvaluationHistory,
  DashboardEntity,
  ScoreInput,
} from '../types/evaluation';

// ─── Frameworks ──────────────────────────────────────────────────────────

export type EvaluationFrequency = 'quarterly' | 'monthly';

export const frameworksApi = {
  list: async (params?: { frequency?: EvaluationFrequency }): Promise<EvaluationFramework[]> => {
    const { data } = await api.get('/evaluation/frameworks/', { params });
    return data.results ?? data;
  },
  get: async (id: number): Promise<EvaluationFramework> => {
    const { data } = await api.get(`/evaluation/frameworks/${id}/`);
    return data;
  },
  create: async (payload: Partial<EvaluationFramework>): Promise<EvaluationFramework> => {
    const { data } = await api.post('/evaluation/frameworks/', payload);
    return data;
  },
  update: async (id: number, payload: Partial<EvaluationFramework>): Promise<EvaluationFramework> => {
    const { data } = await api.patch(`/evaluation/frameworks/${id}/`, payload);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/frameworks/${id}/`);
  },
  pending: async (): Promise<EvaluationFramework[]> => {
    const { data } = await api.get('/evaluation/frameworks/pending/');
    return data.results ?? data;
  },
  submitForApproval: async (id: number): Promise<EvaluationFramework> => {
    const { data } = await api.post(`/evaluation/frameworks/${id}/submit_for_approval/`);
    return data;
  },
  approve: async (id: number): Promise<EvaluationFramework> => {
    const { data } = await api.post(`/evaluation/frameworks/${id}/approve/`);
    return data;
  },
  returnFramework: async (id: number, notes: string): Promise<EvaluationFramework> => {
    const { data } = await api.post(`/evaluation/frameworks/${id}/return_framework/`, { notes });
    return data;
  },
};

// ─── Axes ────────────────────────────────────────────────────────────────

export const axesApi = {
  list: async (frameworkId?: number): Promise<EvaluationAxis[]> => {
    const params = frameworkId ? { framework: frameworkId } : {};
    const { data } = await api.get('/evaluation/axes/', { params });
    return data.results ?? data;
  },
  create: async (payload: Partial<EvaluationAxis>): Promise<EvaluationAxis> => {
    const { data } = await api.post('/evaluation/axes/', payload);
    return data;
  },
  update: async (id: number, payload: Partial<EvaluationAxis>): Promise<EvaluationAxis> => {
    const { data } = await api.patch(`/evaluation/axes/${id}/`, payload);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/axes/${id}/`);
  },
};

// ─── Criteria ────────────────────────────────────────────────────────────

export const criteriaApi = {
  list: async (axisId?: number): Promise<EvaluationCriteria[]> => {
    const params = axisId ? { axis: axisId } : {};
    const { data } = await api.get('/evaluation/criteria/', { params });
    return data.results ?? data;
  },
  create: async (payload: Partial<EvaluationCriteria>): Promise<EvaluationCriteria> => {
    const { data } = await api.post('/evaluation/criteria/', payload);
    return data;
  },
  update: async (id: number, payload: Partial<EvaluationCriteria>): Promise<EvaluationCriteria> => {
    const { data } = await api.patch(`/evaluation/criteria/${id}/`, payload);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/criteria/${id}/`);
  },
};

// ─── Performance Levels ──────────────────────────────────────────────────

export const levelsApi = {
  list: async (criteriaId?: number): Promise<PerformanceLevel[]> => {
    const params = criteriaId ? { criteria: criteriaId } : {};
    const { data } = await api.get('/evaluation/levels/', { params });
    return data.results ?? data;
  },
  create: async (payload: Partial<PerformanceLevel>): Promise<PerformanceLevel> => {
    const { data } = await api.post('/evaluation/levels/', payload);
    return data;
  },
  update: async (id: number, payload: Partial<PerformanceLevel>): Promise<PerformanceLevel> => {
    const { data } = await api.patch(`/evaluation/levels/${id}/`, payload);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/levels/${id}/`);
  },
};

// ─── Cycles ──────────────────────────────────────────────────────────────

export const cyclesApi = {
  list: async (params?: {
    year?: number | string;
    period?: string;
    frequency?: EvaluationFrequency;
  }): Promise<EvaluationCycle[]> => {
    const { data } = await api.get('/evaluation/cycles/', { params });
    return data.results ?? data;
  },
  get: async (id: number): Promise<EvaluationCycle> => {
    const { data } = await api.get(`/evaluation/cycles/${id}/`);
    return data;
  },
  create: async (payload: Partial<EvaluationCycle>): Promise<EvaluationCycle> => {
    const { data } = await api.post('/evaluation/cycles/', payload);
    return data;
  },
  update: async (id: number, payload: Partial<EvaluationCycle>): Promise<EvaluationCycle> => {
    const { data } = await api.patch(`/evaluation/cycles/${id}/`, payload);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/cycles/${id}/`);
  },
  launch: async (id: number): Promise<EvaluationCycle> => {
    const { data } = await api.patch(`/evaluation/cycles/${id}/launch/`);
    return data;
  },
  assign: async (
    id: number,
    assignments: Array<{ department_id: number; evaluator_id: number }>
  ) => {
    const { data } = await api.post(`/evaluation/cycles/${id}/assign/`, { assignments });
    return data;
  },
  duplicate: async (id: number): Promise<EvaluationCycle> => {
    const { data } = await api.post(`/evaluation/cycles/${id}/duplicate/`);
    return data;
  },
};

// ─── Evaluator: Assignments & Scoring ────────────────────────────────────

export const assignmentsApi = {
  list: async (): Promise<EvaluationAssignment[]> => {
    const { data } = await api.get('/evaluation/assignments/');
    return data.results ?? data;
  },
  myAssignments: async (cycleId?: number): Promise<EvaluationAssignment[]> => {
    const params = cycleId ? { cycle: cycleId } : {};
    const { data } = await api.get('/evaluation/assignments/my_assignments/', { params });
    return data;
  },
  getForm: async (assignmentId: number): Promise<EvaluationFormData> => {
    const { data } = await api.get(`/evaluation/assignments/${assignmentId}/form/`);
    return data;
  },
  saveScores: async (assignmentId: number, scores: ScoreInput[]): Promise<CriteriaScore[]> => {
    const { data } = await api.post(`/evaluation/assignments/${assignmentId}/scores/`, { scores });
    return data;
  },
  saveDraft: async (assignmentId: number): Promise<EntityEvaluation> => {
    const { data } = await api.patch(`/evaluation/assignments/${assignmentId}/save_draft/`);
    return data;
  },
  submit: async (assignmentId: number): Promise<EntityEvaluation> => {
    const { data } = await api.post(`/evaluation/assignments/${assignmentId}/submit/`);
    return data;
  },
};

// ─── Evaluations (Approver + Read) ───────────────────────────────────────

export const evaluationsApi = {
  list: async (): Promise<EntityEvaluation[]> => {
    const { data } = await api.get('/evaluation/evaluations/');
    return data.results ?? data;
  },
  get: async (id: number): Promise<EntityEvaluation> => {
    const { data } = await api.get(`/evaluation/evaluations/${id}/`);
    return data;
  },
  detail: async (id: number): Promise<EntityEvaluation> => {
    const { data } = await api.get(`/evaluation/evaluations/${id}/detail_view/`);
    return data;
  },
  pendingApprovals: async (): Promise<EntityEvaluation[]> => {
    const { data } = await api.get('/evaluation/evaluations/pending_approvals/');
    return data;
  },
  approve: async (id: number): Promise<EntityEvaluation> => {
    const { data } = await api.post(`/evaluation/evaluations/${id}/approve/`);
    return data;
  },
  returnEval: async (id: number, notes: string): Promise<EntityEvaluation> => {
    const { data } = await api.post(`/evaluation/evaluations/${id}/return_evaluation/`, { notes });
    return data;
  },
  history: async (id: number): Promise<EvaluationHistory[]> => {
    const { data } = await api.get(`/evaluation/evaluations/${id}/history/`);
    return data;
  },
  downloadReport: async (id: number): Promise<Blob> => {
    const { data } = await api.get(`/evaluation/evaluations/${id}/report/`, {
      responseType: 'blob',
    });
    return data;
  },
  uploadReport: async (id: number, file: File | Blob, filename?: string): Promise<EntityEvaluation> => {
    const form = new FormData();
    // expo-document-picker on web returns a File; on native we may pass a
    // Blob — RN's FormData accepts a `{ uri, name, type }` shape but on web
    // we send the File directly. Caller handles the platform-specific shim.
    if ((file as any).uri && filename) {
      // RN native shape
      form.append('file', {
        uri: (file as any).uri,
        name: filename,
        type: (file as any).mimeType ?? 'application/octet-stream',
      } as any);
    } else {
      form.append('file', file as Blob, filename);
    }
    const { data } = await api.post(
      `/evaluation/evaluations/${id}/report/`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },
  deleteReport: async (id: number): Promise<void> => {
    await api.delete(`/evaluation/evaluations/${id}/report/`);
  },
};

// ─── Dashboard ───────────────────────────────────────────────────────────

// Per-department blended performance score ("وفق التقييم" chart).
export interface BlendComparisonEntity {
  department_id: number;
  department_name: string;
  commitment_rate: number;
  evaluator_score: number;
  final_score: number;
  level_name: string | null;
  level_color: string | null;
}

export interface BlendComparisonLevel {
  order: number;
  name_ar: string;
  color: string;
  score_min: number;
  score_max: number;
}

export interface BlendComparisonResponse {
  entities: BlendComparisonEntity[];
  levels: BlendComparisonLevel[];
  weights: { commitment_weight: number; evaluator_weight: number };
}

export interface BlendComparisonParams {
  cycle?: number;
  period?: string;
  year?: number | string;
  to_date?: string;
  department?: number;
}

// Raw approved-evaluation result per entity ("وفق نتائج التقييم" chart).
// Straight evaluator overall_score — no commitment blend.
export interface EvaluationComparisonEntity {
  department_id: number;
  department_name: string;
  overall_score: number | string | null;
  approved_at: string | null;
}

/** `{ period: null, year: null }` when no evaluation has been completed yet. */
export interface LatestCompletedSlot {
  period: string | null;
  year: number | null;
}

export const evaluationDashboardApi = {
  list: async (params?: {
    cycle?: number;
    period?: string;
    year?: number;
    status?: string;
    frequency?: EvaluationFrequency;
  }): Promise<DashboardEntity[]> => {
    const { data } = await api.get('/evaluation/dashboard/', { params });
    return data;
  },
  /**
   * Most recent (period, year) that holds APPROVED, scored evaluations,
   * scoped to the caller. Used to pick the dashboard's landing period for
   * read-only viewers, who would otherwise open on an unscored quarter.
   */
  latestCompleted: async (params?: {
    frequency?: EvaluationFrequency;
  }): Promise<LatestCompletedSlot> => {
    const { data } = await api.get('/evaluation/dashboard/latest-completed/', { params });
    return data;
  },
  comparison: async (cycleId?: number): Promise<EvaluationComparisonEntity[]> => {
    const params = cycleId ? { cycle: cycleId } : {};
    const { data } = await api.get('/evaluation/dashboard/comparison/', { params });
    return data;
  },
  blendComparison: async (params?: BlendComparisonParams): Promise<BlendComparisonResponse> => {
    const { data } = await api.get('/evaluation/dashboard/blend-comparison/', { params });
    return data;
  },
};

// ─── Monthly Score Scale (admin-managed via Django admin) ────────────────

export interface MonthlyScoreScale {
  id: number;
  scores: number[];     // exactly 15 ascending integers
  band_names: string[]; // 3 names — seed defaults for level names
  band_colors: string[]; // 3 hex codes — seed defaults for level colors
  is_active: boolean;
  updated_at: string;
}

export const scoreScaleApi = {
  get: async (): Promise<MonthlyScoreScale> => {
    const { data } = await api.get('/evaluation/score-scale/');
    if (Array.isArray(data)) return data[0];
    if (data && Array.isArray(data.results)) return data.results[0];
    return data;
  },
};
